import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const TermsOfServicePage = () => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // If there's a hash in the URL, scroll to that section after a brief delay
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Terms of Service</h1>
            <Link 
              to="/" 
              className="inline-flex items-center px-4 py-2 bg-primary-600 dark:bg-primary-50 dark:bg-primary-900/200 text-white text-sm font-medium rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              ← Back to Blip
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 dark:text-gray-300 text-lg mb-8">
                <strong>Last updated:</strong> {new Date().toLocaleDateString()}
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  By accessing and using Blip (the "Platform"), including the mobile application (Android and iOS) and web platform, 
                  you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, 
                  please do not use this service.
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Blip is a location-based exploration platform that allows users to discover places of interest with curated content 
                  generated by local guides and content creators. The Platform consists of:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mt-3">
                  <li><strong>Mobile Application:</strong> Available for Android and iOS devices for exploring points of interest (POIs) and consuming content</li>
                  <li><strong>Web Platform:</strong> A content creation and management platform for validated content creators</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">2. Mobile Application Use</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The Blip mobile application provides a privacy-first experience for exploring your surroundings:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li><strong>No Account Required:</strong> The mobile app does not require you to create an account to use it</li>
                  <li><strong>Local Settings:</strong> All user preferences and settings are stored locally on your device and are not synced to our servers</li>
                  <li><strong>Location Permissions:</strong> The app requests location permissions to show relevant POIs in your area. You may decline precise location permissions and still use the app, though POI accuracy may be reduced</li>
                  <li><strong>Exploration Modes:</strong> The app offers two modes:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li><em>Explore Now:</em> Navigate and discover POIs at your own pace</li>
                      <li><em>Always Explore:</em> Enable background notifications for relevant POIs in your area (configurable in settings)</li>
                    </ul>
                  </li>
                  <li><strong>Child Mode:</strong> Toggle child-friendly content for POIs, tailored to different age groups and interests</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                  The mobile application is for personal, non-commercial use only. You may not reverse engineer, modify, or create derivative works of the application.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">3. Content Creator Accounts</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The web platform allows users to become content creators by creating an account. Content creator accounts are subject to the following:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li><strong>Registration:</strong> You may sign up using Google, Meta, Apple, or email authentication</li>
                  <li><strong>Profile Information:</strong> You must provide your name and may optionally link your social media profiles (Instagram, Facebook, TikTok, website, etc.)</li>
                  <li><strong>Validation Process:</strong> All new content creator accounts are subject to validation by the Blip team before you can create POIs or cards. You will be notified via email once your account is validated</li>
                  <li><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and all activities under your account</li>
                  <li><strong>Accurate Information:</strong> You must provide accurate, complete, and current information at all times</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">4. Content Creation and Ownership</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Validated content creators can create Points of Interest (POIs) and content cards. The following rules apply:
                </p>
                
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">4.1 Creating POIs</h3>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>POIs include the name, location on map, and detailed description (used for validation only)</li>
                  <li>All POIs are subject to validation before being displayed in the mobile app</li>
                  <li>Once published, POIs become public and cannot be deleted by the creator</li>
                  <li>You may set a future publication date for new POIs, during which time you have exclusive access to create cards for them</li>
                  <li>POIs remain in your validation queue until approved by the Blip team</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">4.2 Creating Content Cards</h3>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Cards can contain images, text, audio, video, or combinations thereof</li>
                  <li>You may create cards for existing POIs or POIs you've created</li>
                  <li>You can choose to associate your account and social media links with your cards</li>
                  <li>Cards are subject to validation before publication</li>
                  <li>You retain full ownership and control over your card content and may edit or delete your cards at any time</li>
                  <li>Cards can have a scheduled publication date</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">4.3 Content Standards</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                  All user-generated content must:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Be accurate and not misleading</li>
                  <li>Not contain abusive, offensive, or inappropriate content</li>
                  <li>Not violate any intellectual property rights</li>
                  <li>Comply with all applicable laws and regulations</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                  Content that violates these standards will be rejected during validation or removed if discovered after publication.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">5. Automated Content and AI</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  In addition to user-generated content, Blip uses automated data ingestion and AI technology to enhance the platform:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li><strong>POI Sourcing:</strong> POIs may be automatically sourced from Google Places API, OpenStreetMap, and generative AI to provide comprehensive coverage</li>
                  <li><strong>AI-Generated Cards:</strong> Content cards may be automatically generated using large language models (LLMs) and text-to-speech technology, with information sourced from Wikipedia and other public sources</li>
                  <li><strong>Child-Friendly Content:</strong> Child mode cards are generated using AI to create age-appropriate stories and descriptions</li>
                  <li><strong>Quality Assurance:</strong> Automated systems and AI models regularly validate POI accuracy, location data, and content relevance</li>
                  <li><strong>Content Moderation:</strong> All content, whether user-generated or AI-generated, is subject to automated and manual validation processes</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                  While we strive for accuracy, AI-generated content may occasionally contain errors. Users should verify important information independently.
                </p>
              </section>

              <section className="mb-8" id="cookies">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">6. Cookies and Local Storage</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The Blip web platform uses local storage and similar technologies to enhance your experience and maintain essential functionality:
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">6.1 What We Store</h3>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>Authentication Tokens:</strong> When you log in to the web platform, we store a secure authentication token in your browser's local storage to keep you logged in across sessions</li>
                  <li><strong>Language Preferences:</strong> Your selected language preference is stored locally so the platform displays content in your preferred language</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">6.2 Purpose and Duration</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  These storage mechanisms are essential for the platform to function properly:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>Essential Operation:</strong> Authentication tokens are required to maintain your login session and access your content creator account</li>
                  <li><strong>User Experience:</strong> Language preferences improve your experience by remembering your settings</li>
                  <li><strong>Duration:</strong> Data remains in local storage until you log out (which clears the authentication token) or clear your browser data</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">6.3 Your Control</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  You have full control over this locally stored data:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li>Clear it by logging out of your account</li>
                  <li>Delete it through your browser's settings (clear browsing data)</li>
                  <li>Block local storage through browser settings, though this will prevent you from logging in</li>
                </ul>

                <div className="bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 dark:border-primary-400 p-4 mb-4">
                  <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">
                    📱 Mobile App
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    The mobile app does <strong>not use cookies</strong> or require authentication. All data is stored locally on your device and is never transmitted to our servers.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">6.4 Third-Party Services</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We do not use third-party cookies, analytics services, or tracking tools on the web platform. The only external services that may use their own cookies are:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>OAuth Providers:</strong> Google, Meta, and Apple authentication services when you choose to sign in through these providers</li>
                  <li><strong>Content Delivery:</strong> Services used to host and deliver images and videos</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  These services have their own cookie policies, which we do not control.
                </p>
              </section>

              <section className="mb-8" id="data-control">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">7. Your Data and Account Control</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  As a content creator on the Blip platform, <strong>you are in complete control of your data</strong>. We provide you with tools to manage, export, and delete your information at any time.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">7.1 Data Export</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  You can download all your personal data at any time from your Profile Settings page:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>Profile Information:</strong> Your name, email, bio, phone number, and social media links</li>
                  <li><strong>Content Data:</strong> All POIs and cards you've created, including metadata and publication status</li>
                  <li><strong>Account Details:</strong> Your account creation date, role, and validation status</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The export feature provides a comprehensive JSON file containing all your data in a structured, machine-readable format. This allows you to:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li>Keep a personal backup of your content</li>
                  <li>Review what information Blip stores about you</li>
                  <li>Transfer your data to another service if needed</li>
                  <li>Exercise your data portability rights under GDPR and similar regulations</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">7.2 Account Deletion</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  You can permanently delete your account at any time from your Profile Settings page:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>Immediate Action:</strong> Account deletion takes effect immediately upon confirmation</li>
                  <li><strong>Data Removal:</strong> Your personal information, profile, and account credentials are permanently deleted from our servers</li>
                  <li><strong>Content Cards:</strong> Any content cards you created will be removed from the platform</li>
                  <li><strong>POIs:</strong> Points of Interest you created remain on the platform (as they may have cards from other creators), but attribution to your account is removed</li>
                  <li><strong>No Recovery:</strong> This action is permanent and cannot be undone</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Before deleting your account, we recommend exporting your data if you wish to keep a record of your content.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">7.3 Additional Rights</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  In addition to data export and account deletion, you have the following rights:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>Edit Your Data:</strong> Update your profile information, content cards, and account settings at any time</li>
                  <li><strong>Content Management:</strong> Edit or delete individual content cards you've created</li>
                  <li><strong>Access Restriction:</strong> Choose which social media links and personal information to display publicly</li>
                  <li><strong>Communication Preferences:</strong> Control email notifications and account-related communications</li>
                </ul>

                <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 p-4 mb-6">
                  <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">
                    ✅ Your Rights, Your Control
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    We believe you should have complete control over your data. Access your Profile Settings page to:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 mt-2 ml-4">
                    <li>Export all your data as a JSON file</li>
                    <li>Delete your account and all associated data</li>
                    <li>Update your profile and content at any time</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 mt-3">
                    No requests to support needed - these features are available to you directly in your account settings.
                  </p>
                </div>
              </section>

              <section className="mb-8" id="audit-logging">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">8. Security and Audit Logging</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  To ensure the security and integrity of the platform, <strong>we maintain audit logs for logged-in users of the web platform</strong>. 
                  This applies only to content creators who have registered an account and are using the web interface.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">8.1 What We Log</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  For security and operational purposes, we log the following information when you use the web platform:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>Authentication Events:</strong> Login, logout, and registration attempts</li>
                  <li><strong>Content Actions:</strong> Creation and modification of POIs and content cards</li>
                  <li><strong>Administrative Actions:</strong> Content approval/rejection and role changes (admin accounts only)</li>
                  <li><strong>Action Details:</strong> Timestamp, action type, result (success/failure), and affected resource</li>
                  <li><strong>Technical Information:</strong> IP address, browser/device information (user agent), HTTP method, and response status</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">8.2 Purpose of Audit Logging</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Audit logs serve essential security and operational purposes:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li><strong>Security:</strong> Detect and investigate unauthorized access attempts, suspicious activity, and potential security breaches</li>
                  <li><strong>Compliance:</strong> Maintain records for regulatory compliance and legal requirements</li>
                  <li><strong>Accountability:</strong> Track content creation and modification to maintain platform integrity</li>
                  <li><strong>Support:</strong> Help troubleshoot account and content issues when users request assistance</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">8.3 Data Retention</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Audit logs are retained for operational and security purposes. When you delete your account:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                  <li>Your personal information and content are removed as described in Section 7.2</li>
                  <li>Audit logs are anonymized by removing the direct link to your user account</li>
                  <li>Technical logs (IP addresses, timestamps, actions) may be retained for security and compliance purposes</li>
                </ul>

                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 p-4 mb-6">
                  <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">
                    📱 Mobile App Users
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Audit logging does NOT apply to mobile app users.</strong> The mobile app operates without authentication and does not transmit user data to our servers. 
                    Audit logging is exclusively for logged-in users of the web platform.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">9. Privacy Policy</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Your privacy is important to us. Please read our detailed <Link to="/privacy" className="text-primary-600 hover:underline font-semibold">Privacy Policy</Link> to understand how Blip handles your information 
                  when you use our location-based exploration platform.
                </p>

                <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 p-4 mb-6">
                  <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">
                    ✅ Privacy-First Design
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    The mobile app operates without requiring an account and does <strong>not collect, store, or transmit personal data</strong> to our servers. 
                    All location processing and settings are handled locally on your device. Only the web platform for content creators requires an account.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 mt-2">
                    <Link to="/privacy" className="text-primary-600 hover:underline font-semibold">
                      Read the full Privacy Policy →
                    </Link>
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">10. Prohibited Uses</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  You may not use the Platform:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                  <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                  <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                  <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                  <li>To submit false or misleading information, including inaccurate POI locations or content</li>
                  <li>To create POIs or cards with inappropriate, offensive, or abusive content</li>
                  <li>To attempt to circumvent the content validation process</li>
                  <li>To scrape, crawl, or use automated systems to extract data from the Platform without authorization</li>
                  <li>To impersonate another content creator or falsely represent your credentials</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">11. Content Validation and Moderation</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  All POIs and cards submitted by content creators are subject to validation before publication:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>The Blip team reviews all submissions for accuracy, relevance, and appropriateness</li>
                  <li>Validation may use automated systems, AI models, and manual review processes</li>
                  <li>Content creators are notified of validation status via email and can track pending items in their dashboard</li>
                  <li>We reserve the right to reject or remove content that violates these Terms or our content standards</li>
                  <li>Repeated violations may result in account suspension or termination</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">12. Limitation of Liability</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  To the maximum extent permitted by applicable law:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Blip and its suppliers are not liable for any damages arising from your use or inability to use the Platform</li>
                  <li>POI location information and content cards are provided "as is" without warranties of accuracy or completeness</li>
                  <li>Users are responsible for verifying POI information and exercising appropriate judgment when exploring locations</li>
                  <li>AI-generated content is provided for informational purposes and may contain errors or inaccuracies</li>
                  <li>We are not responsible for user-generated content, though we maintain the right to moderate and remove it</li>
                  <li>The Platform is intended to enhance exploration experiences but should not be solely relied upon for critical decisions</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                  Some jurisdictions do not allow limitations on implied warranties or the exclusion of certain damages. 
                  If these laws apply to you, some or all of the above disclaimers or limitations may not apply.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">13. Contact Information</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Email:</strong> support@blip-platform.com
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">14. Changes to Terms</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
                  If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. 
                  What constitutes a material change will be determined at our sole discretion.
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                  Content creators will be notified of changes via email. Mobile app users are encouraged to check this page periodically for updates.
                  Your continued use of the Platform following the posting of changes constitutes acceptance of those changes.
                </p>
              </section>

              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 text-center">
                  By using Blip, you agree to these Terms of Service. If you have questions about specific features, content creation, 
                  or platform functionality, please refer to our documentation or contact us using the information provided above.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;